import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Icon,
  Input,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'

type Sayfa = components['schemas']['PageRead']
type Ayarlar = components['schemas']['SettingsRead']

/** Sayfanın içeriği nereden geliyor — listede tek satırlık açıklama. */
function kaynak(sayfa: Sayfa): string {
  if (sayfa.kind === 'home') return `Şablon · ${sayfa.blocks.length} blok`
  if (sayfa.kind === 'auto') {
    const modul: Record<string, string> = {
      boards: 'Kurul & komisyon modülünden',
      membership_application: 'Başvuru formu',
      events: 'Etkinlik paketi',
      contact: 'İletişim bilgileri',
    }
    return `Otomatik · ${modul[sayfa.auto_source ?? ''] ?? 'modülden'}`
  }
  return `Sayfa · ${sayfa.blocks.length} blok`
}

/**
 * Web Sitesi › Genel — YES-184 (Figma: `Panel · Web Sitesi / Genel`).
 *
 * Derneğin sitesine tek bakışta cevap veren ekran: yayında mı, hangi şablon,
 * hangi sayfalar var ve hangileri menüde. Blok düzenleme ayrı ekranda.
 */
export function WebSitesi() {
  const slug = useTenantSlug()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [yeniSayfa, setYeniSayfa] = useState(false)
  const [hatalar, setHatalar] = useState<Record<string, string>>({})

  const site = useQuery({
    queryKey: ['site', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/site')
      return data
    },
  })

  const tazele = () => queryClient.invalidateQueries({ queryKey: ['site', slug] })

  const hataGoster = (baslik: string) => (error: unknown) =>
    toast({
      title: baslik,
      description: error instanceof ApiError ? error.problem.detail : undefined,
      tone: 'error',
    })

  const sablonSec = useMutation({
    mutationFn: async (template: Ayarlar['template']) => {
      await api.PATCH('/api/v1/site/settings', { body: { template } })
    },
    onSuccess: () => {
      toast({ title: 'Şablon değişti', tone: 'success' })
      void tazele()
    },
    onError: hataGoster('Şablon değiştirilemedi'),
  })

  const siteYayinla = useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/site/publish', {})
    },
    onSuccess: () => {
      toast({ title: 'Siteniz yayında', tone: 'success' })
      void tazele()
    },
    onError: hataGoster('Yayınlanamadı'),
  })

  const sayfaEkle = useMutation({
    mutationFn: async (form: FormData) => {
      await api.POST('/api/v1/site/pages', {
        body: { title: String(form.get('title') ?? '').trim() },
      })
    },
    onSuccess: () => {
      setYeniSayfa(false)
      setHatalar({})
      toast({ title: 'Sayfa eklendi', tone: 'success' })
      void tazele()
    },
    onError: (error) => {
      if (error instanceof ApiError) setHatalar(error.fieldErrors())
      hataGoster('Sayfa eklenemedi')(error)
    },
  })

  const sayfaGuncelle = useMutation({
    mutationFn: async ({ id, ...govde }: { id: string; in_menu?: boolean }) => {
      await api.PATCH('/api/v1/site/pages/{page_id}', {
        params: { path: { page_id: id } },
        body: govde,
      })
    },
    onSuccess: tazele,
    onError: hataGoster('Kaydedilemedi'),
  })

  const yayinDurumu = useMutation({
    mutationFn: async ({ id, yayinla }: { id: string; yayinla: boolean }) => {
      const yol = yayinla
        ? '/api/v1/site/pages/{page_id}/publish'
        : '/api/v1/site/pages/{page_id}/unpublish'
      await api.POST(yol, { params: { path: { page_id: id } } })
    },
    onSuccess: tazele,
    onError: hataGoster('Değiştirilemedi'),
  })

  const sayfaSil = useMutation({
    mutationFn: async (id: string) => {
      await api.DELETE('/api/v1/site/pages/{page_id}', { params: { path: { page_id: id } } })
    },
    onSuccess: () => {
      toast({ title: 'Sayfa silindi', tone: 'success' })
      void tazele()
    },
    onError: hataGoster('Silinemedi'),
  })

  const sirala = useMutation({
    mutationFn: async (page_ids: string[]) => {
      await api.POST('/api/v1/site/pages/order', { body: { page_ids } })
    },
    onSuccess: tazele,
    onError: hataGoster('Sıra değiştirilemedi'),
  })

  if (site.isError) {
    const yetkisiz = site.error instanceof ApiError && site.error.problem.status === 403
    return (
      <Sayfa>
        <Card>
          <CardBody className="text-sm">
            {yetkisiz ? 'Web sitesini görme yetkiniz yok.' : 'Site bilgileri yüklenemedi.'}
          </CardBody>
        </Card>
      </Sayfa>
    )
  }
  if (!site.data) return <Sayfa>{null}</Sayfa>

  const { settings, pages, templates, site_url } = site.data
  const yayindaSayisi = pages.filter((s) => s.status === 'published').length
  const sablonAdi = templates.find((t) => t.code === settings.template)?.label ?? settings.template

  function tasi(sayfa: Sayfa, yon: -1 | 1) {
    const sirali = [...pages].sort((a, b) => a.menu_order - b.menu_order)
    const yer = sirali.findIndex((s) => s.id === sayfa.id)
    const hedef = yer + yon
    if (hedef < 0 || hedef >= sirali.length) return
    const [tasinan] = sirali.splice(yer, 1)
    sirali.splice(hedef, 0, tasinan)
    sirala.mutate(sirali.map((s) => s.id))
  }

  return (
    <Sayfa>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Web Sitesi</h1>
          <p className="flex flex-wrap items-center gap-2 pt-1 text-sm">
            <a
              href={site_url}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-xs text-[var(--ad-primary)] hover:underline"
            >
              {site_url.replace(/^https?:\/\//, '')}
            </a>
            {settings.is_published ? (
              <Badge tone="success">Yayında</Badge>
            ) : (
              <Badge tone="warning">Yayında değil</Badge>
            )}
            <span className="text-[var(--ad-text-muted)]">
              {yayindaSayisi}/{pages.length} sayfa yayında · {sablonAdi.split(' · ')[0]} şablon
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" asChild>
            <a href={site_url} target="_blank" rel="noreferrer">
              Siteyi görüntüle
            </a>
          </Button>
          {settings.is_published ? (
            <Button asChild>
              <Link to="/web-sitesi/duzenleyici" search={{ sayfa: undefined }}>
                Düzenleyiciyi aç
              </Link>
            </Button>
          ) : (
            <Button disabled={siteYayinla.isPending} onClick={() => siteYayinla.mutate()}>
              {siteYayinla.isPending ? 'Yayınlanıyor…' : 'Siteyi yayına al'}
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader
          title="Şablon"
          description="Şablon değiştirmek içeriği silmez; yalnızca düzen ve stil değişir."
        />
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((sablon) => {
              const secili = sablon.code === settings.template
              const [ad, aciklama] = sablon.label.split(' · ')
              return (
                <div
                  key={sablon.code}
                  className={`flex flex-col gap-2 rounded-[var(--radius-card)] border p-4 ${
                    secili
                      ? 'border-[var(--ad-primary)] bg-[var(--ad-primary)]/5'
                      : 'border-[var(--ad-border)]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{ad}</span>
                    {secili && (
                      <Icon name="check" size={16} primaryColor="var(--ad-primary)" aria-hidden />
                    )}
                  </div>
                  <p className="text-xs text-[var(--ad-text-muted)]">{aciklama}</p>
                  <Button
                    size="sm"
                    variant={secili ? 'ghost' : 'secondary'}
                    disabled={secili || sablonSec.isPending}
                    onClick={() => sablonSec.mutate(sablon.code as Ayarlar['template'])}
                  >
                    {secili ? 'Kullanılıyor' : 'Kullan'}
                  </Button>
                </div>
              )
            })}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Sayfalar ve menü"
          description="Sıra, sitenin üst menüsündeki sırayla aynı."
          action={
            <Button size="sm" onClick={() => setYeniSayfa(true)}>
              Sayfa ekle
            </Button>
          }
        />
        <CardBody>
          <ul className="flex flex-col divide-y divide-[var(--ad-border)]">
            {[...pages]
              .sort((a, b) => a.menu_order - b.menu_order)
              .map((sayfa, sira, dizi) => (
                <li key={sayfa.id} className="flex flex-wrap items-center gap-3 py-3 text-sm">
                  <span className="flex flex-col">
                    <button
                      type="button"
                      aria-label="Yukarı taşı"
                      disabled={sira === 0 || sirala.isPending}
                      onClick={() => tasi(sayfa, -1)}
                      className="px-1 text-[var(--ad-text-muted)] disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      aria-label="Aşağı taşı"
                      disabled={sira === dizi.length - 1 || sirala.isPending}
                      onClick={() => tasi(sayfa, 1)}
                      className="px-1 text-[var(--ad-text-muted)] disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{sayfa.title}</span>
                    <span className="block text-xs text-[var(--ad-text-muted)]">
                      /{sayfa.slug} · {kaynak(sayfa)}
                    </span>
                  </span>

                  <label className="flex items-center gap-1.5 text-xs text-[var(--ad-text-muted)]">
                    <input
                      type="checkbox"
                      checked={sayfa.in_menu}
                      onChange={(event) =>
                        sayfaGuncelle.mutate({ id: sayfa.id, in_menu: event.target.checked })
                      }
                    />
                    Menüde
                  </label>

                  {sayfa.status === 'published' ? (
                    <Badge tone={sayfa.has_unpublished_changes ? 'warning' : 'success'}>
                      {sayfa.has_unpublished_changes ? 'Yayınlanmamış değişiklik' : 'Yayında'}
                    </Badge>
                  ) : (
                    <Badge tone="neutral">Taslak</Badge>
                  )}

                  <Button size="sm" variant="ghost" asChild>
                    <Link to="/web-sitesi/duzenleyici" search={{ sayfa: sayfa.id }}>
                      Düzenle
                    </Link>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={yayinDurumu.isPending}
                    onClick={() =>
                      yayinDurumu.mutate({
                        id: sayfa.id,
                        yayinla: sayfa.status !== 'published' || sayfa.has_unpublished_changes,
                      })
                    }
                  >
                    {sayfa.status === 'published' && !sayfa.has_unpublished_changes
                      ? 'Yayından kaldır'
                      : 'Yayınla'}
                  </Button>
                  {sayfa.kind !== 'home' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={sayfaSil.isPending}
                      onClick={() => {
                        if (window.confirm(`"${sayfa.title}" sayfası silinsin mi?`))
                          sayfaSil.mutate(sayfa.id)
                      }}
                    >
                      Sil
                    </Button>
                  )}
                </li>
              ))}
          </ul>
        </CardBody>
      </Card>

      <Dialog
        open={yeniSayfa}
        onOpenChange={(acik) => {
          setYeniSayfa(acik)
          if (!acik) setHatalar({})
        }}
        title="Sayfa ekle"
        description="Adres başlıktan üretilir; düzenleyicide değiştirebilirsiniz."
        footer={
          <>
            <Button variant="secondary" onClick={() => setYeniSayfa(false)}>
              Vazgeç
            </Button>
            <Button type="submit" form="sayfa-formu" disabled={sayfaEkle.isPending}>
              {sayfaEkle.isPending ? 'Ekleniyor…' : 'Ekle'}
            </Button>
          </>
        }
      >
        <form
          id="sayfa-formu"
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            sayfaEkle.mutate(new FormData(event.currentTarget))
          }}
        >
          <Field label="Sayfa başlığı" required error={hatalar.title ?? hatalar.slug}>
            {(props) => <Input name="title" placeholder="Kongre 2026" {...props} />}
          </Field>
        </form>
      </Dialog>
    </Sayfa>
  )
}

function Sayfa({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-4">{children}</div>
}
